import { useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  User, 
  Shield, 
  MapPin, 
  Users, 
  Building, 
  AlertTriangle, 
  GraduationCap,
  Camera,
  Edit,
  Plus,
  Trash2,
  Check,
  X,
  Lock,
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/services/api';
import { formatDate } from '@/lib/utils';
import { PhotoUploadDialog } from './PhotoUploadDialog';
import { AddressType, ContactType, IdentifierType } from '@/types';
import type { 
  PrisonerProfile, 
  Gender,
  MaritalStatus,
  CustodyType,
  SecurityLevel,
  EyeColor,
  HairColor,
  SecurityFlagType,
} from '@/types';

// Label Mappings
const GENDER_LABELS: Record<Gender, string> = {
  MALE: 'Männlich',
  FEMALE: 'Weiblich',
  DIVERSE: 'Divers',
  UNKNOWN: 'Unbekannt',
};

const MARITAL_STATUS_LABELS: Record<MaritalStatus, string> = {
  SINGLE: 'Ledig',
  MARRIED: 'Verheiratet',
  DIVORCED: 'Geschieden',
  WIDOWED: 'Verwitwet',
  REGISTERED_PARTNERSHIP: 'Eingetragene Partnerschaft',
  UNKNOWN: 'Unbekannt',
};

const CUSTODY_TYPE_LABELS: Record<CustodyType, string> = {
  PRETRIAL: 'Untersuchungshaft',
  PRISON: 'Strafhaft',
  SUBSTITUTE: 'Ersatzfreiheitsstrafe',
  PREVENTIVE: 'Maßregelvollzug',
  JUVENILE: 'Jugendstrafvollzug',
  CIVIL: 'Zivilhaft',
  OTHER: 'Sonstige',
};

const SECURITY_LEVEL_LABELS: Record<SecurityLevel, string> = {
  STANDARD: 'Standard',
  ELEVATED: 'Erhöht',
  HIGH: 'Hoch',
  MAXIMUM: 'Maximal',
};

const ADDRESS_TYPE_LABELS: Record<AddressType, string> = {
  LAST_KNOWN: 'Letzte bekannte',
  REGISTERED: 'Meldeadresse',
  PREVIOUS: 'Frühere',
};

const CONTACT_TYPE_LABELS: Record<ContactType, string> = {
  EMERGENCY: 'Notfall',
  FAMILY_MEMBER: 'Angehöriger',
  LEGAL_GUARDIAN: 'Ges. Vertreter',
  LAWYER: 'Anwalt',
  OTHER: 'Sonstige',
};

const IDENTIFIER_TYPE_LABELS: Record<IdentifierType, string> = {
  JUSTICE_ID: 'Justiz-ID',
  EXECUTION_NUMBER: 'Vollstreckungsnummer',
  PREVIOUS_PRISONER_NUMBER: 'Frühere Gefangenennr.',
  EXTERNAL_REFERENCE: 'Externe Referenz',
};

const EYE_COLOR_LABELS: Record<EyeColor, string> = {
  BLUE: 'Blau',
  GREEN: 'Grün',
  BROWN: 'Braun',
  HAZEL: 'Haselnuss',
  GRAY: 'Grau',
  BLACK: 'Schwarz',
  OTHER: 'Andere',
};

const HAIR_COLOR_LABELS: Record<HairColor, string> = {
  BLONDE: 'Blond',
  BROWN: 'Braun',
  BLACK: 'Schwarz',
  RED: 'Rot',
  GRAY: 'Grau',
  WHITE: 'Weiß',
  BALD: 'Glatze',
  OTHER: 'Andere',
};

const SECURITY_FLAG_LABELS: Record<SecurityFlagType, string> = {
  ESCAPE_RISK: 'Fluchtgefahr',
  VIOLENCE_RISK: 'Gewaltbereitschaft',
  SUICIDE_RISK: 'Suizidgefährdung',
  GANG_AFFILIATION: 'Bandenzugehörigkeit',
  MEDIA_ATTENTION: 'Medienaufmerksamkeit',
  VIP: 'Prominenter Fall',
  WITNESS_PROTECTION: 'Zeugenschutz',
  SEPARATION_REQUIRED: 'Trennungsgebot',
};

interface MasterDataViewProps {
  prisonerId: string;
}

function toBrowserAccessibleMinioUrl(url?: string | null): string | undefined {
  if (!url) return undefined;
  return url.replace('http://minio:9000', `${window.location.origin}/minio`);
}

export function MasterDataView({ prisonerId }: MasterDataViewProps) {
  const queryClient = useQueryClient();
  const [activeSection, setActiveSection] = useState<string>('identity');
  const [isPhotoUploadOpen, setIsPhotoUploadOpen] = useState(false);

  const { data: profile, isLoading, error } = useQuery({
    queryKey: ['prisoner-profile', prisonerId],
    queryFn: () => api.getProfile(prisonerId),
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-muted-foreground">Lädt Stammdaten...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-red-500">Fehler beim Laden der Stammdaten: {(error as Error).message}</p>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  const sections = [
    { id: 'identity', label: 'Identität', icon: User },
    { id: 'custody', label: 'Vollzug', icon: Building },
    { id: 'contacts', label: 'Kontakte', icon: Users },
    { id: 'addresses', label: 'Adressen', icon: MapPin },
    { id: 'biometrics', label: 'Biometrie', icon: Camera, protected: true },
    { id: 'security', label: 'Sicherheit', icon: Shield, protected: true },
    { id: 'social', label: 'Soziales', icon: GraduationCap, protected: true },
  ];

  return (
    <div className="space-y-6">
      {/* Section Navigation */}
      <div className="flex flex-wrap gap-2">
        {sections.map((section) => {
          const Icon = section.icon;
          const isAccessible = !section.protected || 
            (section.id === 'biometrics' && profile.permissions.canViewBiometrics) ||
            (section.id === 'security' && profile.permissions.canViewSecurity) ||
            (section.id === 'social' && profile.permissions.canViewSensitiveSocialData);
          
          return (
            <Button
              key={section.id}
              variant={activeSection === section.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveSection(section.id)}
              disabled={!isAccessible}
              className="flex items-center gap-2"
            >
              <Icon className="h-4 w-4" />
              {section.label}
              {section.protected && !isAccessible && (
                <Lock className="h-3 w-3 ml-1 text-muted-foreground" />
              )}
            </Button>
          );
        })}
      </div>

      {/* Photo Upload Dialog */}
      <PhotoUploadDialog
        prisonerId={prisonerId}
        isOpen={isPhotoUploadOpen}
        onClose={() => setIsPhotoUploadOpen(false)}
      />

      {/* Primary Photo & Quick Info */}
      <div className="flex gap-6">
        {profile.primaryPhoto && (
          <div className="w-32 h-40 rounded-lg overflow-hidden border">
            <img
              src={toBrowserAccessibleMinioUrl(profile.primaryPhoto.downloadUrl)}
              alt="Profilbild"
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <Card className="flex-1">
          <CardContent className="pt-6">
            <div className="grid gap-4 md:grid-cols-4">
              <div>
                <p className="text-sm text-muted-foreground">Geschlecht</p>
                <p className="font-medium">
                  {profile.identity ? GENDER_LABELS[profile.identity.gender] : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Geburtsort</p>
                <p className="font-medium">{profile.identity?.birthPlace || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Staatsangehörigkeit</p>
                <p className="font-medium">
                  {profile.identity?.nationalities?.join(', ') || '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Familienstand</p>
                <p className="font-medium">
                  {profile.identity ? MARITAL_STATUS_LABELS[profile.identity.maritalStatus] : '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Section Content */}
      {activeSection === 'identity' && (
        <IdentitySection 
          profile={profile} 
          prisonerId={prisonerId}
          onUploadPhoto={() => setIsPhotoUploadOpen(true)} 
        />
      )}
      {activeSection === 'custody' && (
        <CustodySection profile={profile} />
      )}
      {activeSection === 'contacts' && (
        <ContactsSection profile={profile} prisonerId={prisonerId} />
      )}
      {activeSection === 'addresses' && (
        <AddressesSection profile={profile} prisonerId={prisonerId} />
      )}
      {activeSection === 'biometrics' && profile.permissions.canViewBiometrics && (
        <BiometricsSection profile={profile} />
      )}
      {activeSection === 'security' && profile.permissions.canViewSecurity && (
        <SecuritySection profile={profile} />
      )}
      {activeSection === 'social' && profile.permissions.canViewSensitiveSocialData && (
        <SocialSection profile={profile} />
      )}
    </div>
  );
}

// ============================================
// Section Components
// ============================================

function IdentitySection({ 
  profile, 
  prisonerId,
  onUploadPhoto 
}: { 
  profile: PrisonerProfile; 
  prisonerId: string;
  onUploadPhoto: () => void;
}) {
  const queryClient = useQueryClient();
  const [showAddIdentifier, setShowAddIdentifier] = useState(false);
  const [identifierForm, setIdentifierForm] = useState({
    type: IdentifierType.JUSTICE_ID,
    value: '',
    issuingAuthority: '',
  });

  const createIdentifierMutation = useMutation({
    mutationFn: () =>
      api.createIdentifier(prisonerId, {
        type: identifierForm.type,
        value: identifierForm.value.trim(),
        issuingAuthority: identifierForm.issuingAuthority.trim() || undefined,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['prisoner-profile', prisonerId] });
      setIdentifierForm({
        type: IdentifierType.JUSTICE_ID,
        value: '',
        issuingAuthority: '',
      });
      setShowAddIdentifier(false);
    },
  });

  return (
    <div className="grid gap-6 md:grid-cols-2">
      {/* Grunddaten */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Personendaten</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <DataRow label="Vorname(n)" value={profile.core.firstName} />
          {profile.identity?.middleNames && (
            <DataRow label="Weitere Vornamen" value={profile.identity.middleNames} />
          )}
          <DataRow label="Nachname" value={profile.core.lastName} />
          {profile.identity?.birthName && (
            <DataRow label="Geburtsname" value={profile.identity.birthName} />
          )}
          <DataRow label="Geburtsdatum" value={formatDate(profile.core.dateOfBirth)} />
          <DataRow label="Geburtsort" value={profile.identity?.birthPlace || '-'} />
          <DataRow 
            label="Geschlecht" 
            value={profile.identity ? GENDER_LABELS[profile.identity.gender] : '-'} 
          />
          <DataRow 
            label="Familienstand" 
            value={profile.identity ? MARITAL_STATUS_LABELS[profile.identity.maritalStatus] : '-'} 
          />
          <DataRow 
            label="Staatsangehörigkeit(en)" 
            value={profile.identity?.nationalities?.join(', ') || '-'} 
          />
        </CardContent>
      </Card>

      {/* Identifikatoren */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Identifikatoren</CardTitle>
          {profile.permissions.canEditBasic && (
            <Button variant="outline" size="sm" onClick={() => setShowAddIdentifier((value) => !value)}>
              <Plus className="h-4 w-4 mr-1" />
              {showAddIdentifier ? 'Abbrechen' : 'Hinzufügen'}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {profile.permissions.canEditBasic && showAddIdentifier && (
            <div className="mb-4 rounded-lg border p-3 space-y-3">
              <div className="grid gap-3 md:grid-cols-3">
                <div className="space-y-1">
                  <Label>Typ</Label>
                  <select
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    value={identifierForm.type}
                    onChange={(event) =>
                      setIdentifierForm((current) => ({
                        ...current,
                        type: event.target.value as IdentifierType,
                      }))
                    }
                  >
                    {Object.entries(IDENTIFIER_TYPE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label>Wert</Label>
                  <Input
                    value={identifierForm.value}
                    onChange={(event) =>
                      setIdentifierForm((current) => ({ ...current, value: event.target.value }))
                    }
                    placeholder="z. B. JVA-123456"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Ausstellende Stelle</Label>
                  <Input
                    value={identifierForm.issuingAuthority}
                    onChange={(event) =>
                      setIdentifierForm((current) => ({
                        ...current,
                        issuingAuthority: event.target.value,
                      }))
                    }
                    placeholder="optional"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  size="sm"
                  disabled={!identifierForm.value.trim() || createIdentifierMutation.isPending}
                  onClick={() => createIdentifierMutation.mutate()}
                >
                  {createIdentifierMutation.isPending ? 'Speichert...' : 'Identifier speichern'}
                </Button>
              </div>
            </div>
          )}

          {profile.identifiers.length === 0 ? (
            <p className="text-muted-foreground text-sm">Keine Identifikatoren erfasst</p>
          ) : (
            <div className="space-y-3">
              {profile.identifiers.map((id) => (
                <div key={id.id} className="flex justify-between items-start p-3 bg-muted/50 rounded-lg">
                  <div>
                    <p className="font-medium">{id.value}</p>
                    <p className="text-sm text-muted-foreground">
                      {IDENTIFIER_TYPE_LABELS[id.type]}
                      {id.issuingAuthority && ` • ${id.issuingAuthority}`}
                    </p>
                  </div>
                  <Badge variant={id.isActive ? 'default' : 'secondary'}>
                    {id.isActive ? 'Aktiv' : 'Inaktiv'}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fotos */}
      <Card className="md:col-span-2">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Lichtbilder</CardTitle>
          {profile.permissions.canUploadPhotos && (
            <Button variant="outline" size="sm" onClick={onUploadPhoto}>
              <Camera className="h-4 w-4 mr-1" />
              Foto hochladen
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {profile.photos.length === 0 ? (
            <div className="text-center py-8">
              <Camera className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-sm mb-4">Keine Fotos vorhanden</p>
              {profile.permissions.canUploadPhotos && (
                <Button variant="outline" onClick={onUploadPhoto}>
                  <Camera className="h-4 w-4 mr-2" />
                  Erstes Foto hochladen
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-wrap gap-4">
              {profile.photos.map((photo) => (
                <div 
                  key={photo.id} 
                  className={`relative w-24 h-32 rounded-lg overflow-hidden border-2 cursor-pointer hover:opacity-90 transition-opacity ${
                    photo.isPrimary ? 'border-primary' : 'border-muted'
                  }`}
                >
                  <img
                    src={toBrowserAccessibleMinioUrl(photo.downloadUrl)}
                    alt={photo.description || 'Foto'}
                    className="w-full h-full object-cover"
                  />
                  {photo.isPrimary && (
                    <Badge className="absolute top-1 right-1 text-xs">
                      Primär
                    </Badge>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 text-center">
                    {photo.takenAt ? formatDate(photo.takenAt) : 'Unbekannt'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function CustodySection({ profile }: { profile: PrisonerProfile }) {
  const custody = profile.custodyStatus;
  
  return (
    <div className="grid gap-6 md:grid-cols-2">
      {/* Aktueller Vollzugsstatus */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Aktueller Vollzugsstatus</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <DataRow 
            label="Vollzugsart" 
            value={custody ? CUSTODY_TYPE_LABELS[custody.custodyType] : '-'} 
          />
          <DataRow label="Rechtsstatus" value={custody?.legalStatus || '-'} />
          <DataRow label="Vollzugsbeginn" value={custody?.custodyStart ? formatDate(custody.custodyStart) : '-'} />
          <DataRow label="Vorauss. Ende" value={custody?.expectedEnd ? formatDate(custody.expectedEnd) : '-'} />
          <DataRow label="Strafmaß" value={custody?.sentenceDuration || '-'} />
          <DataRow label="Reststrafe" value={custody?.remainingSentence || '-'} />
        </CardContent>
      </Card>

      {/* Unterbringung */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Aktuelle Unterbringung</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <DataRow label="Anstalt" value={custody?.facilityName || '-'} />
          <DataRow label="Abteilung" value={custody?.department || '-'} />
          <DataRow label="Station" value={custody?.station || '-'} />
          <DataRow label="Haftraum" value={custody?.cellNumber || '-'} />
          <DataRow 
            label="Sicherheitsstufe" 
            value={custody ? SECURITY_LEVEL_LABELS[custody.securityLevel] : '-'} 
          />
        </CardContent>
      </Card>

      {/* Rechtliche Referenzen */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Rechtliche Referenzen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <DataRow label="Gericht" value={custody?.courtName || '-'} />
          <DataRow label="Aktenzeichen" value={custody?.judgmentReference || '-'} />
        </CardContent>
      </Card>

      {/* Unterbringungshistorie */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Unterbringungshistorie</CardTitle>
        </CardHeader>
        <CardContent>
          {profile.custodyHistory.length === 0 ? (
            <p className="text-muted-foreground text-sm">Keine Historie vorhanden</p>
          ) : (
            <div className="space-y-3">
              {profile.custodyHistory.slice(0, 5).map((entry) => (
                <div key={entry.id} className="p-3 bg-muted/50 rounded-lg">
                  <div className="flex justify-between">
                    <p className="font-medium">{entry.facilityName}</p>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(entry.fromDate)} - {entry.toDate ? formatDate(entry.toDate) : 'aktuell'}
                    </p>
                  </div>
                  {entry.cellNumber && (
                    <p className="text-sm text-muted-foreground">
                      {entry.department && `${entry.department} • `}
                      {entry.station && `${entry.station} • `}
                      Haftraum: {entry.cellNumber}
                    </p>
                  )}
                  {entry.reason && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Grund: {entry.reason}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ContactsSection({ profile, prisonerId }: { profile: PrisonerProfile; prisonerId: string }) {
  const queryClient = useQueryClient();
  const [showAddContact, setShowAddContact] = useState(false);
  const [contactForm, setContactForm] = useState({
    type: ContactType.EMERGENCY,
    firstName: '',
    lastName: '',
    relationship: '',
    phone: '',
    email: '',
    address: '',
    isEmergencyContact: false,
  });

  const createContactMutation = useMutation({
    mutationFn: () =>
      api.createContact(prisonerId, {
        type: contactForm.type,
        firstName: contactForm.firstName.trim(),
        lastName: contactForm.lastName.trim(),
        relationship: contactForm.relationship.trim() || undefined,
        phone: contactForm.phone.trim() || undefined,
        email: contactForm.email.trim() || undefined,
        address: contactForm.address.trim() || undefined,
        isEmergencyContact: contactForm.isEmergencyContact,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['prisoner-profile', prisonerId] });
      setContactForm({
        type: ContactType.EMERGENCY,
        firstName: '',
        lastName: '',
        relationship: '',
        phone: '',
        email: '',
        address: '',
        isEmergencyContact: false,
      });
      setShowAddContact(false);
    },
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg">Kontaktpersonen</CardTitle>
          <CardDescription>Angehörige, Notfallkontakte und weitere Kontakte</CardDescription>
        </div>
        {profile.permissions.canEditBasic && (
          <Button variant="outline" size="sm" onClick={() => setShowAddContact((value) => !value)}>
            <Plus className="h-4 w-4 mr-1" />
            {showAddContact ? 'Abbrechen' : 'Kontakt hinzufügen'}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {profile.permissions.canEditBasic && showAddContact && (
          <div className="mb-4 rounded-lg border p-3 space-y-3">
            <div className="grid gap-3 md:grid-cols-3">
              <div className="space-y-1">
                <Label>Typ</Label>
                <select
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  value={contactForm.type}
                  onChange={(event) =>
                    setContactForm((current) => ({
                      ...current,
                      type: event.target.value as ContactType,
                    }))
                  }
                >
                  {Object.entries(CONTACT_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Vorname</Label>
                <Input
                  value={contactForm.firstName}
                  onChange={(event) =>
                    setContactForm((current) => ({ ...current, firstName: event.target.value }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Nachname</Label>
                <Input
                  value={contactForm.lastName}
                  onChange={(event) =>
                    setContactForm((current) => ({ ...current, lastName: event.target.value }))
                  }
                />
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <Label>Beziehung</Label>
                <Input
                  value={contactForm.relationship}
                  onChange={(event) =>
                    setContactForm((current) => ({ ...current, relationship: event.target.value }))
                  }
                  placeholder="z. B. Schwester"
                />
              </div>
              <div className="space-y-1">
                <Label>Telefon</Label>
                <Input
                  value={contactForm.phone}
                  onChange={(event) =>
                    setContactForm((current) => ({ ...current, phone: event.target.value }))
                  }
                />
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <Label>E-Mail</Label>
                <Input
                  type="email"
                  value={contactForm.email}
                  onChange={(event) =>
                    setContactForm((current) => ({ ...current, email: event.target.value }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Adresse</Label>
                <Input
                  value={contactForm.address}
                  onChange={(event) =>
                    setContactForm((current) => ({ ...current, address: event.target.value }))
                  }
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={contactForm.isEmergencyContact}
                onChange={(event) =>
                  setContactForm((current) => ({
                    ...current,
                    isEmergencyContact: event.target.checked,
                  }))
                }
              />
              Notfallkontakt
            </label>
            <div className="flex justify-end">
              <Button
                size="sm"
                disabled={
                  !contactForm.firstName.trim() ||
                  !contactForm.lastName.trim() ||
                  createContactMutation.isPending
                }
                onClick={() => createContactMutation.mutate()}
              >
                {createContactMutation.isPending ? 'Speichert...' : 'Kontakt speichern'}
              </Button>
            </div>
          </div>
        )}

        {profile.contacts.length === 0 ? (
          <p className="text-muted-foreground text-sm">Keine Kontaktpersonen erfasst</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {profile.contacts.map((contact) => (
              <div key={contact.id} className="p-4 border rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium">{contact.firstName} {contact.lastName}</p>
                    <p className="text-sm text-muted-foreground">
                      {CONTACT_TYPE_LABELS[contact.type]}
                      {contact.relationship && ` • ${contact.relationship}`}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    {contact.isEmergencyContact && (
                      <Badge variant="destructive" className="text-xs">
                        Notfall
                      </Badge>
                    )}
                  </div>
                </div>
                {contact.phone && (
                  <p className="text-sm">📞 {contact.phone}</p>
                )}
                {contact.email && (
                  <p className="text-sm">✉️ {contact.email}</p>
                )}
                {contact.address && (
                  <p className="text-sm text-muted-foreground mt-1">{contact.address}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AddressesSection({ profile, prisonerId }: { profile: PrisonerProfile; prisonerId: string }) {
  const queryClient = useQueryClient();
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [addressForm, setAddressForm] = useState({
    type: AddressType.LAST_KNOWN,
    street: '',
    houseNumber: '',
    postalCode: '',
    city: '',
    country: 'DE',
    isCurrent: true,
  });

  const createAddressMutation = useMutation({
    mutationFn: () =>
      api.createAddress(prisonerId, {
        type: addressForm.type,
        street: addressForm.street.trim() || undefined,
        houseNumber: addressForm.houseNumber.trim() || undefined,
        postalCode: addressForm.postalCode.trim() || undefined,
        city: addressForm.city.trim() || undefined,
        country: addressForm.country.trim() || undefined,
        isCurrent: addressForm.isCurrent,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['prisoner-profile', prisonerId] });
      setAddressForm({
        type: AddressType.LAST_KNOWN,
        street: '',
        houseNumber: '',
        postalCode: '',
        city: '',
        country: 'DE',
        isCurrent: true,
      });
      setShowAddAddress(false);
    },
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg">Adressen</CardTitle>
          <CardDescription>Bekannte Anschriften des Gefangenen</CardDescription>
        </div>
        {profile.permissions.canEditBasic && (
          <Button variant="outline" size="sm" onClick={() => setShowAddAddress((value) => !value)}>
            <Plus className="h-4 w-4 mr-1" />
            {showAddAddress ? 'Abbrechen' : 'Adresse hinzufügen'}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {profile.permissions.canEditBasic && showAddAddress && (
          <div className="mb-4 rounded-lg border p-3 space-y-3">
            <div className="grid gap-3 md:grid-cols-3">
              <div className="space-y-1">
                <Label>Adressart</Label>
                <select
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  value={addressForm.type}
                  onChange={(event) =>
                    setAddressForm((current) => ({
                      ...current,
                      type: event.target.value as AddressType,
                    }))
                  }
                >
                  {Object.entries(ADDRESS_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Straße</Label>
                <Input
                  value={addressForm.street}
                  onChange={(event) =>
                    setAddressForm((current) => ({ ...current, street: event.target.value }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Hausnummer</Label>
                <Input
                  value={addressForm.houseNumber}
                  onChange={(event) =>
                    setAddressForm((current) => ({ ...current, houseNumber: event.target.value }))
                  }
                />
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <div className="space-y-1">
                <Label>PLZ</Label>
                <Input
                  value={addressForm.postalCode}
                  onChange={(event) =>
                    setAddressForm((current) => ({ ...current, postalCode: event.target.value }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Ort</Label>
                <Input
                  value={addressForm.city}
                  onChange={(event) =>
                    setAddressForm((current) => ({ ...current, city: event.target.value }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Land</Label>
                <Input
                  value={addressForm.country}
                  onChange={(event) =>
                    setAddressForm((current) => ({
                      ...current,
                      country: event.target.value.toUpperCase(),
                    }))
                  }
                  placeholder="ISO-Code, z. B. DE"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={addressForm.isCurrent}
                onChange={(event) =>
                  setAddressForm((current) => ({ ...current, isCurrent: event.target.checked }))
                }
              />
              Als aktuelle Adresse markieren
            </label>
            <div className="flex justify-end">
              <Button
                size="sm"
                disabled={
                  (!addressForm.city.trim() && !addressForm.street.trim()) ||
                  createAddressMutation.isPending
                }
                onClick={() => createAddressMutation.mutate()}
              >
                {createAddressMutation.isPending ? 'Speichert...' : 'Adresse speichern'}
              </Button>
            </div>
          </div>
        )}

        {profile.addresses.length === 0 ? (
          <p className="text-muted-foreground text-sm">Keine Adressen erfasst</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {profile.addresses.map((address) => (
              <div key={address.id} className="p-4 border rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <Badge variant="outline">{ADDRESS_TYPE_LABELS[address.type]}</Badge>
                  {address.isCurrent && (
                    <Badge variant="default" className="text-xs">Aktuell</Badge>
                  )}
                </div>
                <p className="font-medium">
                  {address.street} {address.houseNumber}
                </p>
                <p>
                  {address.postalCode} {address.city}
                </p>
                {address.country && (
                  <p className="text-muted-foreground">{address.country}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BiometricsSection({ profile }: { profile: PrisonerProfile }) {
  const bio = profile.biometrics;
  
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-lg">Biometrische Merkmale</CardTitle>
          <Badge variant="secondary">
            <Lock className="h-3 w-3 mr-1" />
            Geschützt
          </Badge>
        </div>
        <CardDescription>
          Körperliche Beschreibung und identifizierende Merkmale
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!bio ? (
          <p className="text-muted-foreground text-sm">Keine biometrischen Daten erfasst</p>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <DataRow label="Körpergröße" value={bio.heightCm ? `${bio.heightCm} cm` : '-'} />
              <DataRow label="Augenfarbe" value={bio.eyeColor ? EYE_COLOR_LABELS[bio.eyeColor] : '-'} />
              <DataRow label="Haarfarbe" value={bio.hairColor ? HAIR_COLOR_LABELS[bio.hairColor] : '-'} />
              <DataRow 
                label="Fingerabdruck" 
                value={bio.fingerprintAvailable ? 'Vorhanden' : 'Nicht erfasst'} 
              />
            </div>
            <div>
              <p className="text-sm font-medium mb-2">Besondere Kennzeichen</p>
              <p className="text-sm text-muted-foreground">
                {bio.distinguishingMarks || 'Keine besonderen Kennzeichen erfasst'}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SecuritySection({ profile }: { profile: PrisonerProfile }) {
  const security = profile.security;
  
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-lg">Sicherheits-Flags</CardTitle>
          <Badge variant="secondary">
            <Lock className="h-3 w-3 mr-1" />
            Streng Vertraulich
          </Badge>
        </div>
        <CardDescription>
          Sicherheitsrelevante Einstufungen und Warnungen
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!security || security.activeCount === 0 ? (
          <div className="flex items-center gap-2 text-green-600">
            <Check className="h-5 w-5" />
            <span>Keine aktiven Sicherheits-Flags</span>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">
                  {security.activeCount} aktive Sicherheits-Flag{security.activeCount > 1 ? 's' : ''}
                </p>
                <p className="text-sm text-amber-700">
                  Maximaler Schweregrad: {security.maxSeverity}/5
                </p>
              </div>
            </div>
            
            <div className="space-y-3">
              {security.activeFlags.map((flag) => (
                <div key={flag.id} className="flex justify-between items-start p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">{SECURITY_FLAG_LABELS[flag.flagType]}</p>
                    <p className="text-sm text-muted-foreground">
                      Gesetzt am: {formatDate(flag.setAt)}
                    </p>
                    {flag.reason && (
                      <p className="text-sm mt-1">{flag.reason}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant={flag.severity >= 4 ? 'destructive' : flag.severity >= 3 ? 'default' : 'secondary'}
                    >
                      Stufe {flag.severity}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SocialSection({ profile }: { profile: PrisonerProfile }) {
  const queryClient = useQueryClient();
  const social = profile.socialData;
  const canViewSensitive = profile.permissions.canViewSensitiveSocialData;
  const canEditSocial = profile.permissions.canEditSocialData;
  const [form, setForm] = useState({
    education: social?.education || '',
    profession: social?.profession || '',
    languages: (social?.languages || []).join(', '),
    hasChildren: social?.hasChildren === undefined ? 'UNKNOWN' : social.hasChildren ? 'YES' : 'NO',
    numberOfChildren: social?.numberOfChildren ?? '',
    residenceStatus: social?.residenceStatus || '',
    countryOfOrigin: social?.countryOfOrigin || '',
    religion: social?.religion || '',
    familyNotes: social?.familyNotes || '',
  });

  const updateSocialMutation = useMutation({
    mutationFn: () =>
      api.updateSocialData(profile.core.id, {
        education: form.education.trim() || undefined,
        profession: form.profession.trim() || undefined,
        languages: form.languages
          .split(',')
          .map((entry) => entry.trim())
          .filter(Boolean),
        hasChildren: form.hasChildren === 'UNKNOWN' ? undefined : form.hasChildren === 'YES',
        numberOfChildren:
          form.numberOfChildren === '' ? undefined : Number.parseInt(String(form.numberOfChildren), 10),
        residenceStatus: form.residenceStatus.trim() || undefined,
        countryOfOrigin: form.countryOfOrigin.trim().toUpperCase() || undefined,
        religion: canViewSensitive ? form.religion.trim() || undefined : undefined,
        familyNotes: canViewSensitive ? form.familyNotes.trim() || undefined : undefined,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['prisoner-profile', profile.core.id] });
    },
  });
  
  return (
    <div className="grid gap-6 md:grid-cols-2">
      {/* Bildung & Beruf */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Bildung & Beruf</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <DataRow
            label="Schulbildung"
            value={
              canEditSocial ? (
                <Input
                  value={form.education}
                  onChange={(event) => setForm((prev) => ({ ...prev, education: event.target.value }))}
                  placeholder="optional"
                />
              ) : (
                social?.education || '-'
              )
            }
          />
          <DataRow
            label="Beruf/Ausbildung"
            value={
              canEditSocial ? (
                <Input
                  value={form.profession}
                  onChange={(event) => setForm((prev) => ({ ...prev, profession: event.target.value }))}
                  placeholder="optional"
                />
              ) : (
                social?.profession || '-'
              )
            }
          />
          <DataRow
            label="Sprachkenntnisse"
            value={
              canEditSocial ? (
                <Input
                  value={form.languages}
                  onChange={(event) => setForm((prev) => ({ ...prev, languages: event.target.value }))}
                  placeholder="Deutsch, Englisch"
                />
              ) : (
                social?.languages?.join(', ') || '-'
              )
            }
          />
        </CardContent>
      </Card>

      {/* Familie */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Familie</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <DataRow
            label="Kinder"
            value={
              canEditSocial ? (
                <select
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  value={form.hasChildren}
                  onChange={(event) => setForm((prev) => ({ ...prev, hasChildren: event.target.value }))}
                >
                  <option value="UNKNOWN">Nicht angegeben</option>
                  <option value="YES">Ja</option>
                  <option value="NO">Nein</option>
                </select>
              ) : social?.hasChildren === true ? (
                `Ja${social.numberOfChildren ? ` (${social.numberOfChildren})` : ''}`
              ) : social?.hasChildren === false ? (
                'Nein'
              ) : (
                '-'
              )
            }
          />
          {canEditSocial ? (
            <DataRow
              label="Anzahl Kinder"
              value={
                <Input
                  type="number"
                  min={0}
                  max={20}
                  value={form.numberOfChildren}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, numberOfChildren: event.target.value }))
                  }
                />
              }
            />
          ) : null}
          {canViewSensitive && (canEditSocial || social?.familyNotes) && (
            <DataRow
              label="Anmerkungen"
              value={
                canEditSocial ? (
                  <Textarea
                    value={form.familyNotes}
                    onChange={(event) => setForm((prev) => ({ ...prev, familyNotes: event.target.value }))}
                    rows={2}
                  />
                ) : (
                  social?.familyNotes || '-'
                )
              }
            />
          )}
        </CardContent>
      </Card>

      {/* Aufenthaltsstatus (für Ausländer) */}
      {(canEditSocial || social?.residenceStatus || social?.countryOfOrigin) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Aufenthaltsstatus</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <DataRow
              label="Herkunftsland"
              value={
                canEditSocial ? (
                  <Input
                    value={form.countryOfOrigin}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, countryOfOrigin: event.target.value.toUpperCase() }))
                    }
                    placeholder="ISO-Code, z. B. DE"
                  />
                ) : (
                  social?.countryOfOrigin || '-'
                )
              }
            />
            <DataRow
              label="Aufenthaltsstatus"
              value={
                canEditSocial ? (
                  <Input
                    value={form.residenceStatus}
                    onChange={(event) => setForm((prev) => ({ ...prev, residenceStatus: event.target.value }))}
                    placeholder="optional"
                  />
                ) : (
                  social?.residenceStatus || '-'
                )
              }
            />
          </CardContent>
        </Card>
      )}

      {/* Religion (sensibel) */}
      {canViewSensitive && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg">Religion</CardTitle>
              <Badge variant="secondary">
                <Lock className="h-3 w-3 mr-1" />
                Streng Vertraulich
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <DataRow
              label="Religionszugehörigkeit"
              value={
                canEditSocial ? (
                  <Input
                    value={form.religion}
                    onChange={(event) => setForm((prev) => ({ ...prev, religion: event.target.value }))}
                    placeholder="optional"
                  />
                ) : (
                  social?.religion || '-'
                )
              }
            />
          </CardContent>
        </Card>
      )}

      {canEditSocial && (
        <Card className="md:col-span-2">
          <CardContent className="pt-6">
            <div className="flex justify-end">
              <Button onClick={() => updateSocialMutation.mutate()} disabled={updateSocialMutation.isPending}>
                {updateSocialMutation.isPending ? 'Speichert...' : 'Sozialdaten speichern'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Helper Component
function DataRow({ label, value }: { label: string; value: string | ReactNode }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
